import { Injectable, computed, signal } from '@angular/core';
import { RowSelection } from '@app/model-composer/models/customization.model';
import { ModelConfig } from '@app/model-composer/models/model-config.model';
import {IpeDebug} from "@app/model-composer/ipe-debug";

type Placement = string;
type SelectionKey = string;

type InternalRowSelection = RowSelection & {
  revision?: number;
};

type RowMeta = {
  key: SelectionKey;
  backendRowId: string;
  eligiblePlacements: string[];
  updateAlso: string[];
};

export type SelectionStateSnapshot = {
  overrides?: Record<string, InternalRowSelection>;
  elementOverrides?: Record<string, InternalRowSelection>;
  rowMeta?: Record<SelectionKey, RowMeta>;
  revision?: number;
};

@Injectable({ providedIn: 'root' })
export class CompositionSelectionService {
  private _defaults = signal<Record<string, InternalRowSelection>>({});
  private _overrides = signal<Record<string, InternalRowSelection>>({});


  private _elementDefaults = signal<Record<string, InternalRowSelection>>({});
  private _elementOverrides = signal<Record<string, InternalRowSelection>>({});


  private _rowMeta = signal<Record<SelectionKey, RowMeta>>({});
  private _revision = signal<number>(0);
  public overrides = this._overrides.asReadonly();
  public elementOverrides = this._elementOverrides.asReadonly();
  public rowMeta = this._rowMeta.asReadonly();
  public revision = this._revision.asReadonly();

  readonly selections = computed<Record<string, RowSelection>>(() => ({
    ...this._defaults(),
    ...this._elementDefaults(),
    ...this._overrides(),
    ...this._elementOverrides(),
  }));

  readonly placementSuffixMap = computed<ReadonlyMap<Placement, string>>(() =>
    this.buildPlacementMap(sel => this.formatVestaSuffix(sel))
  );

  readonly placementCodeMaterialColorMap = computed<ReadonlyMap<Placement, string>>(() =>
    this.buildPlacementMap(sel => this.formatCodeMaterialColor(sel))
  );

  private nextRevision(): number {
    const next = this._revision() + 1;
    this._revision.set(next);
    return next;
  }

  private nonEmptyPlacements(value?: string[] | null): string[] | null {
    return Array.isArray(value) && value.length > 0 ? [...value] : null;
  }

  private resolveEligiblePlacements(
    argsEligiblePlacements: string[] | undefined,
    selectionEligiblePlacements: string[] | undefined,
    metaEligiblePlacements?: string[]
  ): string[] {
    return [
      ...(this.nonEmptyPlacements(argsEligiblePlacements)
        ?? this.nonEmptyPlacements(selectionEligiblePlacements)
        ?? this.nonEmptyPlacements(metaEligiblePlacements)
        ?? [])
    ];
  }

  private selectionDebugSnapshot(selection: RowSelection | null | undefined): any {
    if (!selection) return null;

    return {
      optionId: selection.optionId,
      materialColor: selection.materialColor,
      eligiblePlacements: [...(selection.eligiblePlacements ?? [])],
      item: selection.item,
    };
  }

  private buildPlacementMap(
    formatter: (sel: RowSelection) => string | null
  ): ReadonlyMap<Placement, string> {
    const map = new Map<Placement, string>();

    const applySelections = (entries: Array<[string, InternalRowSelection]>): void => {
      for (const [, sel] of entries) {
        const value = formatter(sel);
        if (!value) { continue; }

        for (const plc of (sel.eligiblePlacements ?? [])) {
          map.set(plc, value);
        }
      }
    };

    applySelections(Object.entries(this._defaults()));
    applySelections(Object.entries(this._elementDefaults()));

    const sortedOverrides = [
      ...Object.entries(this._overrides()),
      ...Object.entries(this._elementOverrides()),
    ].sort(([, a], [, b]) => (a.revision ?? 0) - (b.revision ?? 0));

    applySelections(sortedOverrides);

    return map;
  }

  private formatVestaSuffix(sel: RowSelection): string | null {
    const mat = this.normalizeVestaCode(sel?.item?.material?.materialCampcode);
    const col = this.normalizeVestaCode(sel?.item?.color?.vestaCode);
    if (!mat || !col) { return null; }
    return `${mat}-${col}`;
  }

  private formatCodeMaterialColor(sel: RowSelection): string | null {
    return sel?.item?.codeMaterialColor ?? null;
  }

  private elementKey(uid: string, rowId: string | number): string {
    return `${uid}::${rowId}`;
  }

  private cloneSelectionWithPlacements(
    selection: RowSelection,
    eligiblePlacements: string[],
    revision?: number
  ): InternalRowSelection {
    const current = selection as InternalRowSelection;

    return {
      ...selection,
      eligiblePlacements,
      revision: revision ?? current.revision,
    };
  }

  private withRevision(selection: RowSelection, revision = this.nextRevision()): InternalRowSelection {
    return {
      ...(selection as InternalRowSelection),
      revision,
    };
  }

  private rowBackendId(row: any): string {
    return String(row?.backendRowId ?? row?.id);
  }

  private registerMeta(meta: RowMeta): void {
    this._rowMeta.update(m => ({
      ...m,
      [meta.key]: meta
    }));
  }

  private getMeta(key: SelectionKey): RowMeta | null {
    return this._rowMeta()[key] ?? null;
  }

  private keysByBackendRowId(backendRowId: string): SelectionKey[] {
    const metas = this._rowMeta();
    const out: SelectionKey[] = [];

    for (const key of Object.keys(metas)) {
      if (metas[key].backendRowId === backendRowId) {
        out.push(key);
      }
    }

    return out;
  }

  private setByKey(key: SelectionKey, selection: RowSelection): void {
    const stamped = this.withRevision(selection);
    if (key.includes('::')) {
      this._elementOverrides.update(s => ({
        ...s,
        [key]: stamped
      }));
      return;
    }
    this._overrides.update(s => ({
      ...s,
      [key]: stamped
    }));
  }

  private setByKeyWithRevision(key: SelectionKey, selection: RowSelection, revision: number): void {
    const stamped = this.withRevision(selection, revision);
    if (key.includes('::')) {
      this._elementOverrides.update(s => ({
        ...s,
        [key]: stamped
      }));
      return;
    }
    this._overrides.update(s => ({
      ...s,
      [key]: stamped
    }));
  }

  private resetByKey(key: SelectionKey): void {
    if (key.includes('::')) {
      this._elementOverrides.update(s => {
        const { [key]: _, ...rest } = s;
        return rest;
      });
      return;
    }

    this._overrides.update(s => {
      const { [key]: _, ...rest } = s;
      return rest;
    });
  }

  getSelection(args: {
    rowId: string | number;
    scopeUid?: string;
  }): RowSelection | null {
    const key = args.scopeUid ? this.elementKey(args.scopeUid, args.rowId) : String(args.rowId);
    return this.selections()[key] ?? null;
  }

  hasDefault(rowId: string | number): boolean {
    return this._defaults()[String(rowId)] != null;
  }

  isOverridden(rowId: string | number): boolean {
    return this._overrides()[String(rowId)] != null;
  }

  setSelection(rowId: string | number, selection: RowSelection): void {
    const id = String(rowId);
    const stamped = this.withRevision(selection);
    this._overrides.update(s => ({
      ...s,
      [id]: stamped
    }));
  }

  resetRow(rowId: string | number): void {
    const id = String(rowId);
    this._overrides.update(s => {
      const { [id]: _, ...rest } = s;
      return rest;
    });
  }

  clearRowCompletely(rowId: string | number): void {
    const id = String(rowId);
    this.resetRow(id);
    this._defaults.update(d => {
      const { [id]: _, ...rest } = d;
      return rest;
    });

    this._rowMeta.update(m => {
      const { [id]: _, ...rest } = m;
      return rest;
    });
  }

  applyDefaultsFromStructure(structure: ModelConfig): void {
    const defaults: Record<string, InternalRowSelection> = {};

    for (const group of structure?.customization ?? []) {
      for (const row of group?.customizationRows ?? []) {
        const rowId = String(row.id);
        const backendRowId = this.rowBackendId(row);
        const def = row.default;
        const selected = def?.option?.selected;
        const placements = def?.placements ?? [];

        this.registerMeta({
          key: rowId,
          backendRowId,
          eligiblePlacements: placements,
          updateAlso: Array.isArray(row?.updateAlso)
            ? row.updateAlso.map((x: any) => String(x))
            : [],
        });

        if (!selected || placements.length === 0) { continue; }

        defaults[rowId] = {
          optionId: row.id,
          materialColor: {
            materialId: selected.material?.materialId,
            colorId: selected.color?.colorId
          },
          item: selected,
          eligiblePlacements: placements,
          revision: 0
        };
      }
    }

    this._defaults.set(defaults);
  }

  getElementSelection(uid: string, rowId: string | number): RowSelection | null {
    return this.selections()[this.elementKey(uid, rowId)] ?? null;
  }

  hasElementDefault(uid: string, rowId: string | number): boolean {
    return this._elementDefaults()[this.elementKey(uid, rowId)] != null;
  }

  isElementOverridden(uid: string, rowId: string | number): boolean {
    return this._elementOverrides()[this.elementKey(uid, rowId)] != null;
  }

  setElementSelection(uid: string, rowId: string | number, selection: RowSelection): void {
    const key = this.elementKey(uid, rowId);
    const stamped = this.withRevision(selection);

    this._elementOverrides.update(s => ({
      ...s,
      [key]: stamped
    }));
  }

  resetElementRow(uid: string, rowId: string | number): void {
    const key = this.elementKey(uid, rowId);
    this._elementOverrides.update(s => {
      const { [key]: _, ...rest } = s;
      return rest;
    });
  }

  clearElement(uid: string): void {
    this._elementDefaults.update(s => {
      const out: Record<string, InternalRowSelection> = {};
      for (const k of Object.keys(s)) {
        if (!k.startsWith(uid + '::')) { out[k] = s[k]; }
      }
      return out;
    });

    this._elementOverrides.update(s => {
      const out: Record<string, InternalRowSelection> = {};
      for (const k of Object.keys(s)) {
        if (!k.startsWith(uid + '::')) { out[k] = s[k]; }
      }
      return out;
    });

    this._rowMeta.update(s => {
      const out: Record<string, RowMeta> = {};
      for (const k of Object.keys(s)) {
        if (!k.startsWith(uid + '::')) { out[k] = s[k]; }
      }
      return out;
    });
  }

  applyDefaultsFromElement(uid: string, element: any): void {
    const defaults = { ...this._elementDefaults() };

    if (element?.customization?.length) {
      for (const customization of element?.customization ?? []) {
        for (const row of customization?.customizationRows ?? []) {
          const key = this.elementKey(uid, row.id);
          const backendRowId = this.rowBackendId(row);
          const def = row?.default;
          const selected = def?.option?.selected;
          const placements = def?.placements ?? [];

          this.registerMeta({
            key,
            backendRowId,
            eligiblePlacements: placements,
            updateAlso: Array.isArray(row?.updateAlso)
              ? row.updateAlso.map((x: any) => String(x))
              : [],
          });

          if (!selected || placements.length === 0) { continue; }

          defaults[key] = {
            optionId: row.id,
            materialColor: {
              materialId: selected.material?.materialId,
              colorId: selected.color?.colorId
            },
            item: selected,
            eligiblePlacements: placements,
            revision: 0
          };
        }
      }
    }

    this._elementDefaults.set(defaults);
  }

  syncSelection(args: {
    rowId: string | number;
    selection: RowSelection;
    scopeUid?: string;
    eligiblePlacements?: string[];
  }): void {
    const sourceKey = args.scopeUid
      ? this.elementKey(args.scopeUid, args.rowId)
      : String(args.rowId);

    const sourceMeta = this.getMeta(sourceKey);

    const revision = this.nextRevision();

    const sourceEligiblePlacements = this.resolveEligiblePlacements(
      args.eligiblePlacements,
      args.selection.eligiblePlacements,
      sourceMeta?.eligiblePlacements
    );

    IpeDebug.event(
      'selection.syncSelection.start',
      {
        rowId: args.rowId,
        scopeUid: args.scopeUid ?? null,
        sourceKey,
        revision,
        sourceMeta,
        argsEligiblePlacements: args.eligiblePlacements ?? null,
        selectionEligiblePlacements: args.selection.eligiblePlacements ?? null,
        resolvedSourceEligiblePlacements: sourceEligiblePlacements,
        selection: this.selectionDebugSnapshot(args.selection),
      },
      { stack: true }
    );

    if (!sourceMeta) {
      const sourceSelection = this.cloneSelectionWithPlacements(
        args.selection,
        sourceEligiblePlacements,
        revision
      );

      this.setByKeyWithRevision(sourceKey, sourceSelection, revision);

      IpeDebug.event('selection.syncSelection.end.noMeta', {
        sourceKey,
        revision,
        sourceSelection: this.selectionDebugSnapshot(sourceSelection),
        overrides: this._overrides(),
        elementOverrides: this._elementOverrides(),
      });

      return;
    }

    const sourceSelection = this.cloneSelectionWithPlacements(
      args.selection,
      sourceEligiblePlacements,
      revision
    );

    this.setByKeyWithRevision(sourceKey, sourceSelection, revision);

    const propagated: any[] = [];

    for (const targetBackendId of sourceMeta.updateAlso) {
      const targetKeys = this.keysByBackendRowId(targetBackendId);

      for (const targetKey of targetKeys) {
        if (targetKey === sourceKey) { continue; }

        const targetMeta = this.getMeta(targetKey);
        if (!targetMeta) { continue; }

        const targetEligiblePlacements = this.resolveEligiblePlacements(
          undefined,
          undefined,
          targetMeta.eligiblePlacements
        );

        const targetSelection = this.cloneSelectionWithPlacements(
          sourceSelection,
          targetEligiblePlacements,
          revision
        );

        this.setByKeyWithRevision(
          targetKey,
          targetSelection,
          revision
        );

        propagated.push({
          reason: 'sourceMeta.updateAlso',
          targetBackendId,
          targetKey,
          targetMeta,
          targetEligiblePlacements,
          targetSelection: this.selectionDebugSnapshot(targetSelection),
        });
      }
    }

    const allMeta = this._rowMeta();

    for (const candidateKey of Object.keys(allMeta)) {
      if (candidateKey === sourceKey) { continue; }

      const candidateMeta = allMeta[candidateKey];
      if (!candidateMeta.updateAlso.includes(sourceMeta.backendRowId)) { continue; }

      const candidateEligiblePlacements = this.resolveEligiblePlacements(
        undefined,
        undefined,
        candidateMeta.eligiblePlacements
      );

      const candidateSelection = this.cloneSelectionWithPlacements(
        sourceSelection,
        candidateEligiblePlacements,
        revision
      );

      this.setByKeyWithRevision(
        candidateKey,
        candidateSelection,
        revision
      );

      propagated.push({
        reason: 'candidateMeta.updateAlso.includes(sourceMeta.backendRowId)',
        candidateKey,
        candidateMeta,
        candidateEligiblePlacements,
        candidateSelection: this.selectionDebugSnapshot(candidateSelection),
      });
    }

    IpeDebug.event('selection.syncSelection.end', {
      sourceKey,
      revision,
      sourceSelection: this.selectionDebugSnapshot(sourceSelection),
      propagated,
      overrides: this._overrides(),
      elementOverrides: this._elementOverrides(),
      rowMeta: this._rowMeta(),
    });
  }

  syncReset(args: {
    rowId: string | number;
    scopeUid?: string;
  }): void {
    const sourceKey = args.scopeUid
      ? this.elementKey(args.scopeUid, args.rowId)
      : String(args.rowId);

    const sourceMeta = this.getMeta(sourceKey);

    this.resetByKey(sourceKey);

    if (!sourceMeta) { return; }

    for (const targetBackendId of sourceMeta.updateAlso) {
      const targetKeys = this.keysByBackendRowId(targetBackendId);
      for (const targetKey of targetKeys) {
        if (targetKey === sourceKey) { continue; }
        this.resetByKey(targetKey);
      }
    }

    const allMeta = this._rowMeta();
    for (const candidateKey of Object.keys(allMeta)) {
      if (candidateKey === sourceKey) { continue; }

      const candidateMeta = allMeta[candidateKey];
      if (!candidateMeta.updateAlso.includes(sourceMeta.backendRowId)) { continue; }

      this.resetByKey(candidateKey);
    }
  }

  resetAll(): void {
    this._overrides.set({});
    this._defaults.set({});
    this._elementDefaults.set({});
    this._elementOverrides.set({});
    this._rowMeta.set({});
    this._revision.set(0);
  }

  private normalizeVestaCode(code?: string | null): string | null {
    if (!code) { return null; }
    return code
      .trim()
      .replace(/\s*-\s*/g, '-')
      // .replace(/\s+/g, '');
  }

  setSelectionState(state: SelectionStateSnapshot): void {
    if (state.overrides) {
      this._overrides.set(structuredClone(state.overrides));
    }
    if (state.elementOverrides) {
      this._elementOverrides.set(structuredClone(state.elementOverrides));
    }
    if (state.rowMeta) {
      this._rowMeta.set(structuredClone(state.rowMeta));
    }
    if (typeof state.revision === 'number') {
      this._revision.set(state.revision);
    }
  }
}
